import { onBeforeUnmount, ref, watch, type Ref } from 'vue';
import { useReducedMotion } from '@/composables/useReducedMotion';

/**
 * Анимация числового счётчика от 0 (или initial) до target.
 *
 * Длительность по умолчанию — токен `motion.duration.countUp` (1200мс).
 * Easing — `motion.easing.out` (`cubic-bezier(0.16, 1, 0.3, 1)`).
 *
 * При `prefers-reduced-motion: reduce` значение мгновенно выставляется
 * в target без requestAnimationFrame-цикла.
 *
 * Возвращает реактивное число (`value`), команды `start()` и `stop()`,
 * и флаг `done` — true, когда достигнуто target.
 */
export interface UseCountUpOptions {
  readonly duration?: number;
  readonly autoStart?: boolean;
  readonly initial?: number;
}

export interface UseCountUpReturn {
  readonly value: Ref<number>;
  readonly done: Ref<boolean>;
  readonly start: () => void;
  readonly stop: () => void;
}

const DEFAULT_DURATION_MS = 1200;

function easeOutExpo(t: number): number {
  // cubic-bezier(0.16, 1, 0.3, 1) — хороший easing для счётчиков.
  // Берём приближение на основе expo-кривой, достаточно для 60fps.
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function toNumber(value: number | Ref<number>): number {
  if (typeof value === 'number') return value;
  return value.value;
}

export function useCountUp(
  target: number | Ref<number>,
  options: UseCountUpOptions = {},
): UseCountUpReturn {
  const duration = options.duration ?? DEFAULT_DURATION_MS;
  const initial = options.initial ?? 0;
  const reduced = useReducedMotion();
  const value = ref<number>(initial);
  const done = ref<boolean>(false);
  let rafId: number | null = null;

  function stop(): void {
    if (rafId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(rafId);
    }
    rafId = null;
  }

  function start(): void {
    stop();
    const to = toNumber(target);

    if (reduced.value) {
      value.value = to;
      done.value = true;
      return;
    }

    if (typeof requestAnimationFrame !== 'function') {
      value.value = to;
      done.value = true;
      return;
    }

    const from = value.value;
    const startTs = performance.now();
    done.value = false;

    const tick = (now: number): void => {
      const elapsed = now - startTs;
      const t = duration <= 0 ? 1 : Math.min(1, elapsed / duration);
      const eased = easeOutExpo(t);
      value.value = from + (to - from) * eased;
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        value.value = to;
        done.value = true;
        rafId = null;
      }
    };

    rafId = requestAnimationFrame(tick);
  }

  if (options.autoStart) {
    // Стартуем в следующем тике, чтобы вызывающий код успел смонтироваться.
    queueMicrotask(start);
  }

  // При смене target — если счётчик ещё идёт, продолжаем к новому значению;
  // если уже завершён, стартуем заново только при autoStart.
  if (typeof target !== 'number') {
    watch(target, () => {
      if (rafId !== null || options.autoStart) start();
    });
  }

  onBeforeUnmount(stop);

  return { value, done, start, stop };
}
